const RefundPolicyPage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6" data-aos="slide-right">
              Refund Policy
            </h1>
            <p className="text-xl text-gray-100" data-aos="slide-left" data-aos-delay="100">
              Last updated: December 18, 2024
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="space-y-8 text-gray-700">
              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  Overview
                </h2>
                <p className="leading-relaxed">
                  At Kommonview, we are committed to delivering exceptional value and quality in all our services.
                  Due to the custom nature of our software development, IT infrastructure, and digital solutions,
                  our refund policy is designed to be fair to both our clients and our team while reflecting the
                  unique characteristics of bespoke technology services.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  1. Custom Software Development & Web Applications
                </h2>
                <p className="leading-relaxed mb-4">
                  Custom software development projects are priced based on specific requirements, scope, and complexity.
                  Due to the personalized nature of these services:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Pre-Development Phase:</strong> Full refund available if project is cancelled before development begins (after initial consultation and proposal approval)</li>
                  <li><strong>During Development (0-25% complete):</strong> 75% refund of remaining balance, minus work completed and third-party costs</li>
                  <li><strong>During Development (25-50% complete):</strong> 50% refund of remaining balance, minus work completed and third-party costs</li>
                  <li><strong>During Development (50%+ complete):</strong> No refund available; project may be paused or scope adjusted by mutual agreement</li>
                  <li><strong>Post-Delivery:</strong> No refunds after final deliverables are accepted; warranty period applies for defects</li>
                </ul>
                <p className="leading-relaxed mt-4 text-sm italic">
                  Note: Project completion percentage is measured by agreed-upon milestones in the project contract.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  2. Subscription-Based Services
                </h2>
                <p className="leading-relaxed mb-4">
                  For ongoing subscription services including SaaS platforms, cloud infrastructure management,
                  and support contracts:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Monthly Subscriptions:</strong> Refunds available within first 7 days of initial subscription; prorated refunds not available for partial months</li>
                  <li><strong>Annual Subscriptions:</strong> Refunds available within first 30 days; after 30 days, prorated refund available minus a 20% administrative fee</li>
                  <li><strong>Cancellation:</strong> Services continue until end of current billing period; no refund for unused time in current period</li>
                  <li><strong>Service Interruptions:</strong> Credits may be issued for documented downtime exceeding SLA commitments</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  3. Digital Advertising Services
                </h2>
                <p className="leading-relaxed mb-4">
                  For digital signage, billboard advertising, and digital marketing campaigns:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Before Campaign Launch:</strong> Full refund minus design costs and third-party fees already incurred</li>
                  <li><strong>After Campaign Launch:</strong> No refund; campaigns may be paused or adjusted for remaining period</li>
                  <li><strong>Ad Space Reservations:</strong> Cancellations made 14+ days before start date: full refund; 7-13 days: 50% refund; less than 7 days: no refund</li>
                  <li><strong>Performance Guarantee:</strong> If agreed-upon metrics are not met due to our fault, credits may be issued for future campaigns</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  4. IT Infrastructure & Cloud Services
                </h2>
                <p className="leading-relaxed mb-4">
                  For infrastructure setup, migration, and consulting services:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>One-time Setup Fees:</strong> Refundable within 7 days if no work has commenced</li>
                  <li><strong>Consultation Services:</strong> Refundable if cancelled at least 48 hours before scheduled session</li>
                  <li><strong>Cloud Migration Projects:</strong> Refund policy follows custom development terms based on project milestones</li>
                  <li><strong>Third-Party Costs:</strong> Non-refundable (AWS, Azure, GCP credits, licenses, etc.)</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  5. Training & Educational Services
                </h2>
                <p className="leading-relaxed mb-4">
                  For DevOps training, Atlassian certification, and technical workshops:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Self-Paced Online Courses:</strong> Full refund within 14 days if less than 20% of content accessed</li>
                  <li><strong>Live Training Sessions:</strong> Full refund if cancelled 7+ days before session; 50% refund if 3-6 days before; no refund within 48 hours</li>
                  <li><strong>Corporate Training:</strong> Customized refund terms specified in training agreement</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  6. Non-Refundable Items
                </h2>
                <p className="leading-relaxed mb-4">
                  The following are not eligible for refunds under any circumstances:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Third-party licenses, software, or services purchased on your behalf</li>
                  <li>Cloud computing credits (AWS, Azure, GCP, etc.)</li>
                  <li>Domain name registrations and SSL certificates</li>
                  <li>Completed deliverables that have been accepted by the client</li>
                  <li>Custom design work that has been approved and delivered</li>
                  <li>Time-based consulting services already rendered</li>
                  <li>Advertising space that has already been displayed</li>
                </ul>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  7. Refund Request Process
                </h2>
                <p className="leading-relaxed mb-4">
                  To request a refund:
                </p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>Submit a refund request through our support portal or email within the eligible timeframe</li>
                  <li>Include your project/invoice number, reason for refund, and relevant documentation</li>
                  <li>Our team will review your request within 5 business days</li>
                  <li>If approved, refunds will be processed within 10 business days</li>
                  <li>Refunds will be issued to the original payment method</li>
                </ol>
                <p className="leading-relaxed mt-4">
                  <strong>Contact for Refund Requests:</strong><br />
                  Email: <a href="mailto:billing@kommonview.com" className="text-primary hover:text-secondary">billing@kommonview.com</a><br />
                  Support Portal: <a href="https://kommonview.atlassian.net/servicedesk/customer/portal/1" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-secondary">kommonview.atlassian.net/servicedesk</a>
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  8. Disputes and Escalation
                </h2>
                <p className="leading-relaxed">
                  If you are not satisfied with the outcome of your refund request, you may escalate to our
                  senior management team. We are committed to finding fair solutions and maintaining positive
                  client relationships. In the rare case that we cannot reach a mutual agreement, disputes may
                  be subject to arbitration as outlined in our Terms of Service.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  9. Service Quality Guarantee
                </h2>
                <p className="leading-relaxed">
                  While our refund policy has specific terms, we stand behind the quality of our work. If you
                  experience significant issues with our services that we cannot resolve, we will work with you
                  to find a fair resolution, which may include partial refunds, service credits, or additional
                  work at no charge.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  10. Changes to Refund Policy
                </h2>
                <p className="leading-relaxed mb-4">
                  Kommonview reserves the right to modify this refund policy at any time. Changes will be
                  effective immediately upon posting to our website. Existing projects and active subscriptions
                  will remain subject to the refund policy in effect at the time of purchase.
                </p>
                <p className="leading-relaxed">
                  Your continued use of our services after changes to this policy constitutes acceptance of the
                  updated terms.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-heading font-bold text-primary mb-4">
                  Contact Information
                </h2>
                <p className="leading-relaxed">
                  Questions about our refund policy? We're here to help:
                </p>
                <div className="mt-4 space-y-2">
                  <p>
                    <strong>Email:</strong> <a href="mailto:info@kommonview.com" className="text-primary hover:text-secondary">info@kommonview.com</a>
                  </p>
                  <p>
                    <strong>Billing Inquiries:</strong> <a href="mailto:billing@kommonview.com" className="text-primary hover:text-secondary">billing@kommonview.com</a>
                  </p>
                  <p>
                    <strong>Support Portal:</strong> <a href="https://kommonview.atlassian.net/servicedesk/customer/portal/1" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-secondary">kommonview.atlassian.net/servicedesk</a>
                  </p>
                  <p>
                    <strong>Phone:</strong> Available upon request for existing clients
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-8">
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong>Note:</strong> This refund policy applies to services purchased directly from Kommonview.
                  Services purchased through third-party partners or resellers may be subject to different terms.
                  Custom enterprise agreements may include specific refund provisions that supersede this general policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicyPage;
